import { Check, Loader2 } from "lucide-react";

interface Props {
  currentPhase: number;
  phase1Done: boolean;
  phase2Done: boolean;
  phase3Done: boolean;
}

const PHASES = [
  { num: 1, label: "Evidence & Phenomena", shortLabel: "Evidence" },
  { num: 2, label: "Structure & Dynamics", shortLabel: "Structure" },
  { num: 3, label: "Therapy & Advice", shortLabel: "Therapy" },
];

export default function PhaseProgressStepper({ currentPhase, phase1Done, phase2Done, phase3Done }: Props) {
  const isDone = (num: number) => num === 1 ? phase1Done : num === 2 ? phase2Done : phase3Done;
  const isActive = (num: number) => currentPhase === num;
  const allDone = phase1Done && phase2Done && phase3Done;

  // Calculate progress percentage for the track fill
  const progressPercent = allDone ? 100 : phase2Done ? 75 : phase1Done ? 37.5 : currentPhase === 1 ? 10 : 0;

  return (
    <div className="rounded-xl border border-border bg-card p-3 md:p-4">
      <div className="relative flex items-center w-full">
        {/* Background track */}
        <div className="absolute left-4 right-4 md:left-5 md:right-5 h-1 bg-muted rounded-full" />
        {/* Filled track */}
        <div
          className="absolute left-4 md:left-5 h-1 bg-primary/60 rounded-full transition-all duration-700 ease-out"
          style={{ width: `calc(${progressPercent}% - 2rem)` }}
        />

        {/* Phase circles */}
        {PHASES.map((p, i) => {
          const done = isDone(p.num);
          const active = isActive(p.num);

          return (
            <div key={p.num} className={`flex flex-col items-center z-10 ${i === 0 ? "" : "flex-1"}`}>
              <div
                className={`
                  w-8 h-8 md:w-9 md:h-9 rounded-full flex items-center justify-center text-xs font-bold transition-all shrink-0 border-2
                  ${done
                    ? "bg-primary text-primary-foreground border-primary shadow-md"
                    : active
                      ? "bg-card text-primary border-primary shadow-sm"
                      : "bg-card text-muted-foreground/50 border-muted"
                  }
                `}
              >
                {done ? (
                  <Check className="h-3.5 w-3.5 md:h-4 md:w-4" />
                ) : active ? (
                  <Loader2 className="h-3.5 w-3.5 md:h-4 md:w-4 animate-spin" />
                ) : (
                  p.num
                )}
              </div>
              <span className={`text-[10px] md:text-xs mt-1.5 text-center leading-tight ${
                done ? "text-primary font-medium" : active ? "text-foreground font-medium" : "text-muted-foreground/60"
              }`}>
                <span className="hidden md:inline">{p.label}</span>
                <span className="md:hidden">{p.shortLabel}</span>
              </span>
            </div>
          );
        })}
      </div>

      {allDone && (
        <p className="text-xs text-primary font-semibold text-center mt-2.5 flex items-center justify-center gap-1">
          <Check className="h-3 w-3" /> Analysis Complete
        </p>
      )}
    </div>
  );
}
